/**
 * Search: board 03.
 *
 * The screen is a workspace rather than a document. One header row carries the
 * query and everything that changes the shape of the answer; a facet rail
 * carries everything that narrows it; a status strip along the bottom says
 * what the search actually did. Between them is the only thing that matters,
 * which is the results.
 *
 * **Results are grouped page-first.** A newspaper page is the thing that was
 * printed, so four matches on one page are one piece of evidence rather than
 * four, and the page row shows the sheet with its hit regions drawn on it
 * before any text is read. Each row expands into the unit cards the board
 * draws, and the Pages / Articles control switches to the flat unit list for
 * a reader who wants ranked items rather than sheets.
 *
 * **The URL is the state.** Every filter is a search parameter, under the API's
 * own names, so a search is shareable by copying the address bar.
 *
 * **Every figure is counted.** Nothing on this screen is a placeholder number.
 * Where the API cannot yet supply a count, the element carrying it is omitted
 * rather than filled in.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  api,
  exportUrl,
  type EditionRow,
  type Facets,
  type LandingFacts,
  type SearchAlternatives,
  type SearchUnitResult,
  type Timeline as TimelineData,
} from '../lib/api'
import { ApiError } from '../lib/api'
import { FilterChip, Segmented } from '../components/Page'
import { useSession } from '../lib/session'
import { FacetRail, type YearBar } from '../components/search/FacetRail'
import { PageRow } from '../components/search/PageRow'
import { ResultCard } from '../components/search/ResultCard'
import { EmptyState, GuestTierBanner, NoResults, type Starter } from '../components/search/States'
import {
  activeFilters,
  adaptFacets,
  adaptSearch,
  apiQuery,
  facetQuery,
  n,
  PER_PAGE,
  readState,
  seconds,
  writeState,
  type AdaptedSearch,
  type SearchState,
} from '../components/search/model'

/** Rows opened without being asked, when the result set is large enough to need folding. */
const OPEN_BY_DEFAULT = 3
/** Below this many pages the whole answer is short enough to read at once. */
const OPEN_ALL_BELOW = 10

export function SearchPage() {
  const [params, setParams] = useSearchParams()
  const state = useMemo(() => readState(params), [params])
  const { can, me } = useSession()

  const [draft, setDraft] = useState(state.q)
  const [railOpen, setRailOpen] = useState(false)
  const [toggled, setToggled] = useState<Record<string, boolean>>({})
  const [clipped, setClipped] = useState<Set<string>>(new Set())
  const [saved, setSaved] = useState(false)

  // Committing a search resets paging and the per-row folding, because a new
  // answer is not the same answer scrolled.
  function commit(next: SearchState) {
    setParams(writeState(next))
    setToggled({})
  }

  function apply(patch: Record<string, string>) {
    const merged = new URLSearchParams(writeState(state))
    for (const [key, value] of Object.entries(patch)) {
      if (value) merged.set(key, value)
      else merged.delete(key)
    }
    merged.delete('offset')
    setParams(merged)
    setToggled({})
    if ('q' in patch) setDraft(patch.q)
  }

  const search = useQuery({
    queryKey: ['search', apiQuery(state)],
    queryFn: async () =>
      adaptSearch(await api.get<Record<string, unknown>>(`/search?${apiQuery(state)}`), state.group),
    enabled: state.q.length > 0,
  })

  const facetsQuery = useQuery({
    queryKey: ['facets', facetQuery(state)],
    queryFn: async () =>
      adaptFacets(await api.get<Record<string, unknown>>(`/search/facets?${facetQuery(state)}`)),
  })

  // The year histogram wants a count per year. The contract puts it on the
  // facets; while that is being built the timeline carries the same
  // measurement under another name, and both are real counts.
  const timeline = useQuery({
    queryKey: ['search-years', state.q],
    queryFn: () =>
      api.get<TimelineData>(`/search/timeline?q=${encodeURIComponent(state.q)}`),
  })

  const alternatives = useQuery({
    queryKey: ['alternatives', state.q],
    queryFn: async () => {
      try {
        return await api.get<SearchAlternatives>(
          `/search/alternatives?q=${encodeURIComponent(state.q)}`,
        )
      } catch (error) {
        // The endpoint is part of the same round of work as the page-grouped
        // search. Until it answers, the no-results panel offers what it can
        // stand behind and promises nothing it has not counted.
        if (error instanceof ApiError && error.status === 404) return null
        throw error
      }
    },
    enabled: state.q.length > 0 && (search.data?.total_units ?? 1) === 0,
    retry: false,
  })

  const facts = useQuery({
    queryKey: ['landing'],
    queryFn: () => api.get<LandingFacts>('/landing'),
    staleTime: 5 * 60 * 1000,
  })

  const editions = useQuery({
    queryKey: ['editions'],
    queryFn: () => api.get<EditionRow[]>('/editions'),
    enabled: state.q.length === 0,
    staleTime: 5 * 60 * 1000,
  })

  const facets = facetsQuery.data
  const data = search.data
  const years = useYears(facets, timeline.data)
  const chips = activeFilters(state, facets)

  async function saveSearch() {
    const name = window.prompt('Name this search', state.q)
    if (!name) return
    await api.post('/saved-searches', {
      name,
      query: Object.fromEntries(writeState(state)),
    })
    setSaved(true)
  }

  async function clip(unit: SearchUnitResult) {
    if (!unit.page_id || !unit.region) return
    await api.post('/clippings', {
      page_id: unit.page_id,
      bbox: unit.region,
      article_id: unit.unit_type === 'article' ? unit.object_id : null,
      note: state.q ? `Found searching for “${state.q}”` : null,
    })
    setClipped((current) => new Set(current).add(unit.id))
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-paper">
      <SearchBar
        state={state}
        draft={draft}
        setDraft={setDraft}
        data={data}
        facts={facts.data}
        loading={search.isFetching}
        canSave={can('research.saved_search')}
        canExport={can('content.export_bulk')}
        saved={saved}
        onSave={() => void saveSearch()}
        onCommit={commit}
        railOpen={railOpen}
        onToggleRail={() => setRailOpen((open) => !open)}
      />

      <div className="flex min-h-0 flex-1">
        <aside
          className={`${railOpen ? 'block' : 'hidden'} w-full flex-none overflow-y-auto rail lg:block lg:w-[276px]`}
          aria-label="Filters"
        >
          <FacetRail
            state={state}
            facets={facets}
            years={years}
            onChange={commit}
            onClear={() => commit({ ...readState(new URLSearchParams()), q: state.q })}
            apiHref={`/api/search?${apiQuery(state)}`}
          />
        </aside>

        <div className={`${railOpen ? 'hidden' : 'flex'} min-w-0 flex-1 flex-col lg:flex`}>
          {!me && data && (data.total_units > 0 || (data.results?.length ?? 0) > 0) && (
            <GuestTierBanner />
          )}

          {(chips.length > 0 || data) && (
            <div className="flex flex-wrap items-center gap-2.5 border-b border-n300 px-[18px] py-[11px]">
              {chips.length > 0 && (
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink2">
                  Active
                </span>
              )}
              {chips.map((chip) => (
                <FilterChip key={chip.key} onRemove={() => commit(chip.clear(state))}>
                  {chip.label}
                </FilterChip>
              ))}
              {data && <ShowingLine state={state} data={data} />}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto">
            {search.isError && (
              <p className="px-[22px] py-8 text-[15px] text-mark">
                The search could not be run: {(search.error as Error).message}
              </p>
            )}

            {state.q.length === 0 && (
              <EmptyState
                facts={facts.data}
                facets={facets}
                starters={startersFrom(facets, editions.data)}
                coverage={coverageFrom(editions.data)}
                gaps={gapsFrom(years)}
                onApply={apply}
              />
            )}

            {state.q.length > 0 && search.isSuccess && data && data.total_units === 0 && (
              <NoResults
                state={state}
                alternatives={alternatives.data ?? undefined}
                onApply={apply}
                reportParams={Object.fromEntries(writeState(state))}
              />
            )}

            {data && data.total_units > 0 && (
              <Results
                state={state}
                data={data}
                toggled={toggled}
                setToggled={setToggled}
                onClip={can('research.clipping') ? (unit) => void clip(unit) : undefined}
                clipped={clipped}
              />
            )}

            {/*
              Paging counts whatever is being listed. In page mode the limit
              applies to pages, so paging against the unit total would offer a
              next page that does not exist.
            */}
            {data && pagedTotal(data) > shownCount(data) + state.offset && (
              <div className="flex items-center gap-2.5 px-4 py-4 sm:px-[18px]">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => commit({ ...state, offset: state.offset + PER_PAGE })}
                >
                  More results
                </button>
                <span className="font-mono text-[10px] uppercase text-ink2">
                  Page {n(Math.floor(state.offset / PER_PAGE) + 1)} of{' '}
                  {n(Math.ceil(pagedTotal(data) / PER_PAGE))} · {PER_PAGE} per page
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <StatusStrip state={state} data={data} facets={facets} />
    </div>
  )
}

// --- the header row ---------------------------------------------------------

function SearchBar({
  state,
  draft,
  setDraft,
  data,
  facts,
  loading,
  canSave,
  canExport,
  saved,
  onSave,
  onCommit,
  railOpen,
  onToggleRail,
}: {
  state: SearchState
  draft: string
  setDraft: (value: string) => void
  data: AdaptedSearch | undefined
  facts: LandingFacts | undefined
  loading: boolean
  canSave: boolean
  canExport: boolean
  saved: boolean
  onSave: () => void
  onCommit: (next: SearchState) => void
  railOpen: boolean
  onToggleRail: () => void
}) {
  const elapsed = seconds(data?.elapsed_ms)
  const editions = data?.editions_searched || facts?.editions
  return (
    <form
      className="flex flex-wrap items-center gap-3 border-b border-n300 px-[15px] py-2.5"
      onSubmit={(event) => {
        event.preventDefault()
        onCommit({ ...state, q: draft, offset: 0 })
      }}
    >
      <div className="flex h-9 min-w-[240px] max-w-[660px] flex-1 items-center gap-2.5 rounded-[--radius-md] border border-rule2 bg-paper px-3 focus-within:border-accent focus-within:shadow-[0_0_0_3px_var(--jl-accent-100)]">
        <span className="font-mono text-[13px] text-ink3" aria-hidden="true">
          ⌕
        </span>
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="a name, a place, an event"
          aria-label="Search the archive"
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[16px] text-ink outline-none placeholder:text-ink4"
        />
        {/*
          The three layer names are the board's hint and, since a reader
          asked why "animal" found "animated", also the switch: a click runs
          that layer alone (exact words only, trigram only, meaning only) and
          a second click returns to the blend. The active one takes the
          accent; the blend leaves all three quiet.
        */}
        <span
          className="hidden flex-none items-center gap-1 font-mono text-[10px] uppercase text-ink2 sm:inline-flex"
          role="group"
          aria-label="Search layer"
        >
          {(['exact', 'fuzzy', 'semantic'] as const).map((layer, index) => (
            <span key={layer} className="inline-flex items-center gap-1">
              {index > 0 && <span aria-hidden="true">·</span>}
              <button
                type="button"
                onClick={() => onCommit({ ...state, mode: state.mode === layer ? 'blend' : layer, offset: 0 })}
                aria-pressed={state.mode === layer}
                title={
                  layer === 'exact'
                    ? 'Only the words as typed, unstemmed. Click again for the full blend.'
                    : layer === 'fuzzy'
                      ? 'Only trigram matching, for damaged spellings.'
                      : 'Only matching by meaning.'
                }
                className={`uppercase ${
                  state.mode === layer
                    ? 'font-semibold text-accent shadow-[0_6px_0_-5px_var(--jl-accent)]'
                    : 'text-ink2 hover:text-body'
                }`}
              >
                {layer}
              </button>
            </span>
          ))}
        </span>
      </div>

      <button type="submit" className="btn-primary flex-none">
        Search
      </button>

      <div className="flex items-baseline gap-[9px] font-mono text-[11px] text-ink2">
        {loading ? (
          <span className="text-ink">Searching…</span>
        ) : data ? (
          <span className="text-[12px] text-ink">
            {n(data.total_units)} {data.total_units === 1 ? 'result' : 'results'}
          </span>
        ) : null}
        {elapsed && <span>· {elapsed}</span>}
        {editions ? (
          <span>
            · {n(editions)} {editions === 1 ? 'edition' : 'editions'}
          </span>
        ) : null}
      </div>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onToggleRail}
          aria-expanded={railOpen}
          className="btn-secondary lg:hidden"
        >
          {railOpen ? 'Results' : 'Filters'}
        </button>

        <Segmented
          size="sm"
          ariaLabel="Group results"
          value={state.group}
          onChange={(group) => onCommit({ ...state, group, offset: 0 })}
          options={[
            { value: 'page', label: 'Pages', title: 'One row per page, with its hits drawn on it' },
            { value: 'unit', label: 'Articles', title: 'One row per matching item, ranked' },
          ]}
        />

        <span className="hidden text-[13px] text-ink2 sm:inline">Sort</span>
        <Segmented
          size="sm"
          ariaLabel="Sort results"
          value={state.sort}
          onChange={(sort) => onCommit({ ...state, sort, offset: 0 })}
          options={[
            { value: 'relevance', label: 'Relevance' },
            { value: 'oldest', label: 'Oldest' },
            { value: 'newest', label: 'Newest' },
          ]}
        />

        {canSave && state.q && (
          <button type="button" className="btn-secondary" onClick={onSave} disabled={saved}>
            {saved ? 'Saved' : 'Save search'}
          </button>
        )}

        {canExport && data && data.total_units > 0 && (
          <details className="relative">
            <summary className="btn-secondary cursor-pointer list-none">
              Export {n(data.total_units)}
            </summary>
            <div className="popover absolute right-0 z-10 mt-1 flex w-[150px] flex-col p-1.5">
              <a
                className="px-2 py-1 text-[13.5px] no-underline"
                href={exportUrl(state.q, 'csv', Object.fromEntries(writeState(state)))}
              >
                CSV
              </a>
              <a
                className="px-2 py-1 text-[13.5px] no-underline"
                href={exportUrl(state.q, 'json', Object.fromEntries(writeState(state)))}
              >
                JSON
              </a>
            </div>
          </details>
        )}
      </div>
    </form>
  )
}

function ShowingLine({ state, data }: { state: SearchState; data: AdaptedSearch }) {
  const shown = shownCount(data)
  if (shown === 0) return null
  const first = state.offset + 1
  const last = state.offset + shown
  const noun = state.group === 'page' ? 'pages' : 'results'
  const total = state.group === 'page' && data.total_pages ? data.total_pages : data.total_units
  return (
    <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.06em] text-ink2">
      Showing {n(first)}–{n(last)} of {n(total)} {noun}
    </span>
  )
}

function shownCount(data: AdaptedSearch): number {
  return data.group === 'page' ? (data.pages?.length ?? 0) : (data.results?.length ?? 0)
}

/** The total of whatever the limit and offset are counting. */
function pagedTotal(data: AdaptedSearch): number {
  return data.group === 'page' ? data.total_pages || data.total_units : data.total_units
}

// --- the results ------------------------------------------------------------

function Results({
  state,
  data,
  toggled,
  setToggled,
  onClip,
  clipped,
}: {
  state: SearchState
  data: AdaptedSearch
  toggled: Record<string, boolean>
  setToggled: (next: Record<string, boolean>) => void
  onClip?: (unit: SearchUnitResult) => void
  clipped: Set<string>
}) {
  if (state.group === 'unit') {
    return (
      <div>
        {(data.results ?? []).map((unit) => (
          <ResultCard
            key={unit.id}
            unit={unit}
            query={state.q}
            onClip={onClip}
            clipped={clipped.has(unit.id)}
          />
        ))}
      </div>
    )
  }

  const pages = data.pages ?? []
  const openAll = pages.length < OPEN_ALL_BELOW
  return (
    <div>
      {pages.map((group, index) => {
        const fallback = openAll || index < OPEN_BY_DEFAULT
        const open = toggled[group.page_id] ?? fallback
        return (
          <PageRow
            key={group.page_id || index}
            group={group}
            query={state.q}
            open={open}
            onToggle={() => setToggled({ ...toggled, [group.page_id]: !open })}
            onClip={onClip}
            clipped={clipped}
          />
        )
      })}
    </div>
  )
}

// --- the status strip -------------------------------------------------------

/**
 * What the search actually did, in the mono. It exists so a researcher can
 * tell a blended answer from an exact one without reading the results, and so
 * a result set narrowed by a licence says so rather than looking short.
 */
function StatusStrip({
  state,
  data,
  facets,
}: {
  state: SearchState
  data: AdaptedSearch | undefined
  facets: Facets | undefined
}) {
  const layers = data?.layers
  const matched = layers
    ? (Object.entries(layers) as [string, number][]).filter(([, count]) => count > 0)
    : []
  const shown = data ? (data.results?.length ?? 0) : 0
  const restricted = (data?.results ?? []).filter((unit) => unit.restricted).length

  return (
    // `.statusbar` scrolls sideways rather than wrapping, so every cell has to
    // refuse to break or the strip grows a second line on a phone.
    <div className="statusbar [&>span]:whitespace-nowrap">
      <span className="text-ink">
        {matched.length > 0
          ? matched.map(([layer, count]) => `${layer} ${n(count)}`).join(' · ')
          : 'tsv_raw + tsv_corrected + embedding'}
      </span>
      <span className="text-rule2">|</span>
      <span>{state.mode === 'blend' ? 'trgm fuzzy available' : `mode ${state.mode}`}</span>
      {shown > 0 && (
        <>
          <span className="text-rule2">|</span>
          <span>
            {n(shown - restricted)} of {n(shown)} shown within licence scope
          </span>
        </>
      )}
      {facets?.licence?.label && (
        <>
          <span className="text-rule2">|</span>
          <span className="uppercase">{facets.licence.label}</span>
        </>
      )}
      <span className="ml-auto whitespace-nowrap">
        crops served from the region endpoint · licence checked per request
      </span>
    </div>
  )
}

// --- derived, counted figures ----------------------------------------------

/**
 * A count per year for the rail's histogram.
 *
 * The contract puts it on `facets.years`; while that is being built the
 * timeline endpoint carries the same measurement, so the histogram is drawn
 * from whichever answered. Years inside the span with no count are filled in
 * as empty bars, because an absent year and a year with nothing in it are
 * different facts and the rail draws them differently.
 */
function useYears(
  facets: Facets | undefined,
  timeline: TimelineData | undefined,
): YearBar[] {
  return useMemo(() => {
    const counted = new Map<number, number>()
    const held = new Set<number>()
    if (facets?.years?.length) {
      for (const entry of facets.years) {
        counted.set(entry.year, entry.count)
        if (entry.editions > 0) held.add(entry.year)
      }
    } else if (timeline?.points?.length) {
      for (const point of timeline.points) {
        const year = Number(point.period.slice(0, 4))
        if (!Number.isFinite(year)) continue
        counted.set(year, (counted.get(year) ?? 0) + point.matches)
        if (point.total > 0) held.add(year)
      }
    }
    if (counted.size === 0) return []
    const keys = [...counted.keys()].sort((a, b) => a - b)
    const first = keys[0]
    const last = keys[keys.length - 1]
    const bar = (year: number): YearBar => ({
      year,
      count: counted.get(year) ?? 0,
      held: held.has(year),
    })
    // A span wider than a working lifetime is a data problem, not a histogram.
    if (last - first > 400) return keys.map(bar)
    const out: YearBar[] = []
    for (let year = first; year <= last; year += 1) out.push(bar(year))
    return out
  }, [facets, timeline])
}

/** Four ways in, each one a real thing in the archive with its real count. */
function startersFrom(facets: Facets | undefined, editions: EditionRow[] | undefined): Starter[] {
  const starters: Starter[] = []

  const dated = (editions ?? []).filter((edition) => edition.pub_date)
  if (dated.length > 0) {
    const latest = dated.reduce((best, edition) =>
      (edition.pub_date ?? '') > (best.pub_date ?? '') ? edition : best,
    )
    const date = latest.pub_date as string
    starters.push({
      kind: 'A date',
      label: formatDate(date),
      note: latest.page_count ? `${n(latest.page_count)} pages` : 'browse the issue',
      params: { q: '', date_from: date, date_to: date },
    })
  }

  const author = (facets?.authors ?? []).filter((entry) => entry.value)[0]
  if (author) {
    starters.push({
      kind: 'A person',
      label: author.label ?? author.value,
      note: `${n(author.count)} ${author.count === 1 ? 'byline' : 'bylines'}`,
      params: { q: author.value, author: author.value },
    })
  }

  const section = (facets?.sections ?? [])[0]
  if (section) {
    starters.push({
      kind: 'A section',
      label: section.label ?? section.value,
      note: `${n(section.count)} results`,
      params: { q: '', section: section.value },
    })
  }

  const type = (facets?.types ?? []).find((entry) => entry.value !== 'article')
  if (type) {
    starters.push({
      kind: 'A type',
      label: `${type.value.charAt(0).toUpperCase()}${type.value.slice(1)}s`,
      note: `${n(type.count)} ${type.value}s`,
      params: { q: '', types: type.value },
    })
  }

  return starters
}

/**
 * What is held and how it was read, counted from the editions themselves.
 *
 * The split is on `route`, the pipeline that actually read the edition, rather
 * than on the source type it was filed as. A scan carrying an OCR layer is
 * still a scan, and it is the pipeline that decides whether the text under a
 * year was read or recognised, which is what the reader is being told.
 */
function coverageFrom(editions: EditionRow[] | undefined): [string, string][] {
  if (!editions || editions.length === 0) return []
  const byKind = new Map<string, string[]>()
  for (const edition of editions) {
    if (!edition.pub_date) continue
    const kind = kindOf(edition)
    const list = byKind.get(kind)
    if (list) list.push(edition.pub_date)
    else byKind.set(kind, [edition.pub_date])
  }
  return [...byKind.entries()].map(([kind, dates]) => {
    const sorted = dates.sort()
    const first = sorted[0].slice(0, 4)
    const last = sorted[sorted.length - 1].slice(0, 4)
    return [first === last ? first : `${first}–${last}`, kind] as [string, string]
  })
}

function kindOf(edition: EditionRow): string {
  if (edition.route === 'A') return 'digital'
  if (edition.route === 'B') return 'scanned'
  return 'route not yet assigned'
}

/**
 * Years inside the span that hold no edition.
 *
 * Listed by year while the list is short enough to read, and summarised as a
 * count when it is not. Either way it is counted; the alternative, which is to
 * leave the holes out, lets a reader mistake an absent decade for a quiet one.
 */
function gapsFrom(years: YearBar[]): [string, string][] {
  const missing = years.filter((entry) => !entry.held).map((entry) => entry.year)
  if (missing.length === 0) return []
  if (missing.length <= 6) {
    return missing.map((year) => [String(year), 'no edition held'] as [string, string])
  }
  const first = years[0].year
  const last = years[years.length - 1].year
  return [[`${first}–${last}`, `${n(missing.length)} years hold none`]]
}

function formatDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return iso
  // Day-month-year, the way a masthead sets a dateline.
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}
